// LocalCardEditModal Component
// Admin modal for editing the pricing page Local card (display name, description, price, linked product)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RevoltModal } from '../../../components/RevoltModal';
import { RevoltButton } from '../../../components/RevoltButton';

const DEFAULT_CONFIG = {
  displayName: 'Local',
  description: '- Run locally\n- Full control',
  price: 0,
  productId: null,
  accessTag: null,
};

export function LocalCardEditModal({ isOpen, onClose, config, onSave }) {
  const [displayName, setDisplayName] = useState(DEFAULT_CONFIG.displayName);
  const [description, setDescription] = useState(DEFAULT_CONFIG.description);
  const [price, setPrice] = useState(String(DEFAULT_CONFIG.price));
  const [productId, setProductId] = useState(null);
  const [accessTag, setAccessTag] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const searchRef = useRef(null);

  const loadConfig = useCallback(() => {
    const c = config || DEFAULT_CONFIG;
    setDisplayName(c.displayName ?? DEFAULT_CONFIG.displayName);
    setDescription(c.description ?? DEFAULT_CONFIG.description);
    setPrice(String(c.price ?? DEFAULT_CONFIG.price));
    setProductId(c.productId || null);
    setAccessTag(c.accessTag ?? '');
    setSelectedProductName('');
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
    setError(null);
  }, [config]);

  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen, loadConfig]);

  useEffect(() => {
    if (!isOpen || !config?.productId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/products/${config.productId}`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && res.ok && data?.product?.name) {
          setSelectedProductName(data.product.name);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [isOpen, config?.productId]);

  const searchProducts = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        page: '1',
        limit: '10',
        includeInactive: 'true',
      });
      const response = await fetch(`/api/admin/products/search?${params.toString()}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSearchResults(data.products || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      setSearchResults([]);
    }
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchProducts(searchQuery);
      debounceRef.current = null;
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, searchProducts]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProduct = (product) => {
    setProductId(product.id);
    setSelectedProductName(product.name || '');
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
  };

  const handleClearProduct = () => {
    setProductId(null);
    setSelectedProductName('');
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const priceNum = parseFloat(price);
      if (isNaN(priceNum) || priceNum < 0) {
        throw new Error('Please enter a valid price (0 or greater)');
      }
      await onSave({
        displayName: displayName.trim() || DEFAULT_CONFIG.displayName,
        description: description.trim() || DEFAULT_CONFIG.description,
        price: priceNum,
        productId: productId || null,
        accessTag: accessTag.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <RevoltModal isOpen={isOpen} onClose={onClose} title="Edit Local Card">
      <div style={{ padding: '20px', minWidth: '400px' }}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#ccc', fontSize: '14px' }}>
            Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px',
            }}
            placeholder="Local"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#ccc', fontSize: '14px' }}>
            Description (one feature per line, start with -)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px',
              resize: 'vertical',
            }}
            placeholder="- Run locally\n- Full control"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#ccc', fontSize: '14px' }}>
            Price (displayed on card)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px',
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }} ref={searchRef}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#ccc', fontSize: '14px' }}>
            Product for &quot;Buy Local&quot; link (where users go when they click)
          </label>
          {selectedProductName ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 12px',
                background: 'rgba(139, 0, 0, 0.2)',
                border: '1px solid rgba(139, 0, 0, 0.4)',
                borderRadius: '6px',
              }}
            >
              <span style={{ flex: 1, color: '#fff' }}>{selectedProductName}</span>
              <button
                type="button"
                onClick={handleClearProduct}
                style={{
                  padding: '4px 10px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '4px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Clear
              </button>
            </div>
          ) : (
            <>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Search products..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                }}
              />
              {searchOpen && searchResults.length > 0 && (
                <ul
                  style={{
                    listStyle: 'none',
                    margin: '4px 0 0',
                    padding: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: 'rgba(0,0,0,0.6)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '6px',
                  }}
                >
                  {searchResults.map((p) => (
                    <li
                      key={p.id}
                      onClick={() => handleSelectProduct(p)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        color: '#fff',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {p.name} {p.price != null && `($${p.price})`}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '6px', color: '#ccc', fontSize: '14px' }}>
            Access tag (if user has this tag, shows &quot;Purchased&quot; in green; leave empty to disable)
          </label>
          <input
            type="text"
            value={accessTag}
            onChange={(e) => setAccessTag(e.target.value)}
            placeholder="e.g. local_access"
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px',
            }}
          />
        </div>

        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 12px',
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              borderRadius: '6px',
              color: '#fca5a5',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <RevoltButton variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </RevoltButton>
          <RevoltButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </RevoltButton>
        </div>
      </div>
    </RevoltModal>
  );
}

export default LocalCardEditModal;
